'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { usePaginationSlider } from '@/app/components/sliders/hooks/usePaginationSlider';
import { usePrevNextSlider } from '@/app/components/sliders/hooks/usePrevNextSlider';
import { SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/icon-button';
import { SPARTAN_PRODUCT_LIST_GAP_CLASS_NAME } from '@/app/lcv/components/sections/spartan/products/shared/list-gap';
import { getSpartanRevealElementProps, SPARTAN_REVEAL_PRESET, SPARTAN_REVEAL_ROLE } from '@/app/lcv/components/sections/spartan/products/shared/reveal';
import { SpartanProductSectionHeader } from '@/app/lcv/components/sections/spartan/products/shared/section-header';
import { useCarouselImageLoading } from '@/app/lcv/components/sections/spartan/shared/hooks/useCarouselImageLoading';
import { SpartanLineupModelCard } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/model-card';
import type { SpartanLineupCarouselProps } from '@/app/lcv/components/sections/spartan/shared/lineup/carousel/types';
import { SPARTAN_LINEUP_CONTROLS_REVEAL_OFFSET } from '@/app/lcv/components/sections/spartan/shared/lineup/constants';
import { SpartanLineupGroupTabs } from '@/app/lcv/components/sections/spartan/shared/lineup/group-tabs';
import styles from '@/app/lcv/components/sections/spartan/shared/lineup/styles.module.css';
import { SPARTAN_LINEUP_DATA_GA, SPARTAN_LINEUP_TRACKING } from '@/app/lcv/components/sections/spartan/shared/lineup/tracking';
import type { SpartanLineupGroupKey } from '@/app/lcv/components/sections/spartan/shared/lineup/types';
import { SpartanSlidePagination } from '@/app/lcv/components/sections/spartan/shared/slide-pagination';
import { SpartanSliderNav } from '@/app/lcv/components/sections/spartan/shared/slider-nav';
import { SPARTAN_SLIDER_NAV_LABELS } from '@/app/lcv/components/sections/spartan/shared/slider-nav/constants';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { stripHtmlTags } from '@/app/utils/format';
import { trackDynamicEvents } from '@/tag-manager';
const CAROUSEL_INTERACTIVE_TARGET_SELECTOR = 'button, input, textarea, select, label, [role="button"], [role="tab"]';
const isCarouselInteractiveTarget = (target: EventTarget | null): boolean => target instanceof Element && Boolean(target.closest(CAROUSEL_INTERACTIVE_TARGET_SELECTOR));
export const SpartanLineupCarousel = t0 => {
  const $ = _c(128);
  const {
    eyebrow,
    titleLead,
    title,
    titleFont: t1,
    groups,
    defaultGroup,
    remark,
    eventName,
    tone: t2,
    styleName,
    isTextRevealEnabled
  } = t0;
  const titleFont = t1 === undefined ? "display" : t1;
  const tone = t2 === undefined ? "light" : t2;
  const isDark = tone === "dark";
  const tabGroupId = useId();
  const panelId = `${tabGroupId}-panel`;
  const tabIdPrefix = `${tabGroupId}-tab`;
  const [activeGroup, setActiveGroup] = useState(defaultGroup);
  const [hasSwitchedGroup, setHasSwitchedGroup] = useState(false);
  const models = groups[activeGroup].models;
  const hasMultipleModels = models.length > 1;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      loop: false,
      align: "start",
      duration: 32,
      inViewThreshold: 0.05,
      watchDrag: _temp
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t3);
  const {
    currentPage,
    selectedIndex,
    onPageChange,
    scrollSnaps,
    slidesInView
  } = usePaginationSlider(emblaApi);
  const [canScroll, setCanScroll] = useState(false);
  const [carouselViewport, setCarouselViewport] = useState(null);
  const [revealedModelIndices, setRevealedModelIndices] = useState(_temp2);
  const [isNextCardHintVisible, setIsNextCardHintVisible] = useState(false);
  const isNextCardHintDismissedRef = useRef(false);
  const hasNextCardHintBeenShownRef = useRef(false);
  const dragStartSnapRef = useRef(null);
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      rootMargin: "0px 0px -28% 0px",
      triggerOnce: true
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    ref: setRevealViewportRef,
    inView: isRevealViewportInView
  } = useInView(t4);
  let t5;
  if ($[2] !== emblaApi || $[3] !== emblaRef || $[4] !== models.length) {
    t5 = {
      emblaApi,
      emblaRef,
      itemCount: models.length
    };
    $[2] = emblaApi;
    $[3] = emblaRef;
    $[4] = models.length;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const {
    getImageLoading,
    setViewportRef
  } = useCarouselImageLoading(t5);
  let t6;
  if ($[6] !== setRevealViewportRef || $[7] !== setViewportRef) {
    t6 = node => {
      setCarouselViewport(node);
      setViewportRef(node);
      setRevealViewportRef(node);
    };
    $[6] = setRevealViewportRef;
    $[7] = setViewportRef;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const setCarouselViewportRef = t6;
  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick
  } = usePrevNextSlider(emblaApi);
  const canUseSliderNavigation = hasMultipleModels && !(prevBtnDisabled && nextBtnDisabled);
  let t7;
  if ($[9] !== carouselViewport || $[10] !== emblaApi) {
    t7 = () => {
      if (!carouselViewport) {
        return;
      }
      const handleUpdateCanScroll = () => {
        const hasOverflow = carouselViewport.scrollWidth > carouselViewport.clientWidth + 1;
        const hasMultipleSnaps = (emblaApi?.scrollSnapList().length ?? 0) > 1;
        setCanScroll(hasOverflow || hasMultipleSnaps);
      };
      handleUpdateCanScroll();
      emblaApi?.on("reInit", handleUpdateCanScroll);
      window.addEventListener("resize", handleUpdateCanScroll);
      const resizeObserver = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(handleUpdateCanScroll);
      resizeObserver?.observe(carouselViewport);
      return () => {
        emblaApi?.off("reInit", handleUpdateCanScroll);
        window.removeEventListener("resize", handleUpdateCanScroll);
        resizeObserver?.disconnect();
      };
    };
    $[9] = carouselViewport;
    $[10] = emblaApi;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== carouselViewport || $[13] !== emblaApi || $[14] !== models.length) {
    t8 = [carouselViewport, emblaApi, models.length];
    $[12] = carouselViewport;
    $[13] = emblaApi;
    $[14] = models.length;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[16] !== isRevealViewportInView || $[17] !== models.length || $[18] !== selectedIndex || $[19] !== slidesInView) {
    t9 = () => {
      if (!isRevealViewportInView) {
        return;
      }
      setRevealedModelIndices(currentIndices => {
        const nextIndices = new Set(currentIndices);
        slidesInView.forEach(index => nextIndices.add(index));
        if (selectedIndex >= 0 && selectedIndex < models.length) {
          nextIndices.add(selectedIndex);
        }
        if (selectedIndex + 1 < models.length) {
          nextIndices.add(selectedIndex + 1);
        }
        return nextIndices.size === currentIndices.size ? currentIndices : nextIndices;
      });
    };
    t10 = [isRevealViewportInView, models.length, selectedIndex, slidesInView];
    $[16] = isRevealViewportInView;
    $[17] = models.length;
    $[18] = selectedIndex;
    $[19] = slidesInView;
    $[20] = t10;
    $[21] = t9;
  } else {
    t10 = $[20];
    t9 = $[21];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[22] !== canScroll || $[23] !== isRevealViewportInView || $[24] !== selectedIndex) {
    t11 = () => {
      const canShowNextCardHint = isRevealViewportInView && canScroll && selectedIndex === 0 && !isNextCardHintDismissedRef.current;
      if (!canShowNextCardHint) {
        if (selectedIndex !== 0 || hasNextCardHintBeenShownRef.current) {
          isNextCardHintDismissedRef.current = true;
        }
        setIsNextCardHintVisible(false);
        return;
      }
      hasNextCardHintBeenShownRef.current = true;
      setIsNextCardHintVisible(true);
    };
    t12 = [canScroll, isRevealViewportInView, selectedIndex];
    $[22] = canScroll;
    $[23] = isRevealViewportInView;
    $[24] = selectedIndex;
    $[25] = t11;
    $[26] = t12;
  } else {
    t11 = $[25];
    t12 = $[26];
  }
  useEffect(t11, t12);
  let t13;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = () => {
      isNextCardHintDismissedRef.current = true;
      setIsNextCardHintVisible(false);
    };
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  const dismissNextCardHint = t13;
  let t14;
  if ($[28] !== activeGroup) {
    t14 = nextGroup => {
      if (nextGroup === activeGroup) {
        return;
      }
      setHasSwitchedGroup(true);
      setActiveGroup(nextGroup);
    };
    $[28] = activeGroup;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const handleGroupChange = t14;
  let t15;
  let t16;
  if ($[30] !== defaultGroup) {
    t15 = () => {
      setHasSwitchedGroup(false);
      setActiveGroup(defaultGroup);
    };
    t16 = [defaultGroup];
    $[30] = defaultGroup;
    $[31] = t15;
    $[32] = t16;
  } else {
    t15 = $[31];
    t16 = $[32];
  }
  useEffect(t15, t16);
  let t17;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = () => {
      setRevealedModelIndices(new Set());
      isNextCardHintDismissedRef.current = false;
      hasNextCardHintBeenShownRef.current = false;
      setIsNextCardHintVisible(false);
    };
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== activeGroup) {
    t18 = [activeGroup];
    $[34] = activeGroup;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  useEffect(t17, t18);
  let t19;
  if ($[36] !== emblaApi) {
    t19 = () => {
      if (!emblaApi) {
        return;
      }
      emblaApi.reInit();
      emblaApi.scrollTo(0, true);
    };
    $[36] = emblaApi;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== activeGroup || $[39] !== emblaApi) {
    t20 = [activeGroup, emblaApi];
    $[38] = activeGroup;
    $[39] = emblaApi;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  useLayoutEffect(t19, t20);
  let t21;
  let t22;
  if ($[41] !== emblaApi || $[42] !== eventName || $[43] !== models) {
    t21 = () => {
      if (!emblaApi) {
        return;
      }
      const handlePointerDown = () => {
        dragStartSnapRef.current = emblaApi.selectedScrollSnap();
      };
      const handlePointerUp = () => {
        const startIndex = dragStartSnapRef.current;
        dragStartSnapRef.current = null;
        if (startIndex === null) {
          return;
        }
        const nextIndex = emblaApi.selectedScrollSnap();
        if (nextIndex === startIndex) {
          return;
        }
        trackDynamicEvents({
          event_name: eventName,
          group_name: SPARTAN_LINEUP_DATA_GA.section,
          step_name: stripHtmlTags(models[nextIndex]?.title ?? ""),
          value: nextIndex > startIndex ? "next" : "prev",
          area: "swipe"
        });
      };
      emblaApi.on("pointerDown", handlePointerDown);
      emblaApi.on("pointerUp", handlePointerUp);
      return () => {
        dragStartSnapRef.current = null;
        emblaApi.off("pointerDown", handlePointerDown);
        emblaApi.off("pointerUp", handlePointerUp);
      };
    };
    t22 = [emblaApi, eventName, models];
    $[41] = emblaApi;
    $[42] = eventName;
    $[43] = models;
    $[44] = t21;
    $[45] = t22;
  } else {
    t21 = $[44];
    t22 = $[45];
  }
  useEffect(t21, t22);
  let t23;
  if ($[46] !== activeGroup || $[47] !== eventName || $[48] !== groups || $[49] !== handleGroupChange || $[50] !== panelId || $[51] !== tabIdPrefix || $[52] !== tone) {
    t23 = <SpartanLineupGroupTabs groups={groups} value={activeGroup} tone={tone} eventName={eventName} panelId={panelId} tabIdPrefix={tabIdPrefix} onChange={handleGroupChange} />;
    $[46] = activeGroup;
    $[47] = eventName;
    $[48] = groups;
    $[49] = handleGroupChange;
    $[50] = panelId;
    $[51] = tabIdPrefix;
    $[52] = tone;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== eyebrow || $[55] !== isTextRevealEnabled || $[56] !== t23 || $[57] !== title || $[58] !== titleFont || $[59] !== titleLead || $[60] !== tone) {
    t24 = <SpartanProductSectionHeader eyebrow={eyebrow} titleLead={titleLead} title={title} titleFont={titleFont} trackingPrefix={SPARTAN_LINEUP_TRACKING.section} tone={tone} isTextRevealEnabled={isTextRevealEnabled} actions={t23} />;
    $[54] = eyebrow;
    $[55] = isTextRevealEnabled;
    $[56] = t23;
    $[57] = title;
    $[58] = titleFont;
    $[59] = titleLead;
    $[60] = tone;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  const t25 = `${tabIdPrefix}-${activeGroup}`;
  let t26;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.groupPanel
    });
    $[62] = t26;
  } else {
    t26 = $[62];
  }
  let t27;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.carouselViewport
    });
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = cn("flex items-stretch", SPARTAN_PRODUCT_LIST_GAP_CLASS_NAME, styles.modelListNextCardHint);
    $[64] = t28;
  } else {
    t28 = $[64];
  }
  const t29 = hasSwitchedGroup ? "" : undefined;
  const t30 = isNextCardHintVisible ? "" : undefined;
  let t31;
  if ($[65] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.modelList
    });
    $[65] = t31;
  } else {
    t31 = $[65];
  }
  let t32;
  if ($[66] !== eventName || $[67] !== getImageLoading || $[68] !== hasSwitchedGroup || $[69] !== models || $[70] !== revealedModelIndices || $[71] !== styleName || $[72] !== tone) {
    let t33;
    if ($[74] !== eventName || $[75] !== getImageLoading || $[76] !== hasSwitchedGroup || $[77] !== revealedModelIndices || $[78] !== styleName || $[79] !== tone) {
      t33 = (model, modelIndex) => <SpartanLineupModelCard key={[model.title, model.href, modelIndex].join("-")} model={model} modelIndex={modelIndex} eventName={eventName} imageLoading={getImageLoading(modelIndex)} tone={tone} styleName={styleName} isCarouselRevealed={hasSwitchedGroup || revealedModelIndices.has(modelIndex)} isGroupSwitchTransition={hasSwitchedGroup} />;
      $[74] = eventName;
      $[75] = getImageLoading;
      $[76] = hasSwitchedGroup;
      $[77] = revealedModelIndices;
      $[78] = styleName;
      $[79] = tone;
      $[80] = t33;
    } else {
      t33 = $[80];
    }
    t32 = models.map(t33);
    $[66] = eventName;
    $[67] = getImageLoading;
    $[68] = hasSwitchedGroup;
    $[69] = models;
    $[70] = revealedModelIndices;
    $[71] = styleName;
    $[72] = tone;
    $[73] = t32;
  } else {
    t32 = $[73];
  }
  let t33;
  if ($[81] !== activeGroup || $[82] !== t29 || $[83] !== t30 || $[84] !== t32) {
    t33 = <ul key={activeGroup} className={t28} data-spartan-lineup-group-switch={t29} data-spartan-lineup-next-card-hint={t30} {...t31}>{t32}</ul>;
    $[81] = activeGroup;
    $[82] = t29;
    $[83] = t30;
    $[84] = t32;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== setCarouselViewportRef || $[87] !== t33) {
    t34 = <div ref={setCarouselViewportRef} onPointerDown={dismissNextCardHint} role="region" aria-roledescription="carousel" aria-label={SPARTAN_SLIDER_NAV_LABELS.carousel} {...t27}>{t33}</div>;
    $[86] = setCarouselViewportRef;
    $[87] = t33;
    $[88] = t34;
  } else {
    t34 = $[88];
  }
  let t35;
  if ($[89] !== canScroll || $[90] !== currentPage || $[91] !== eventName || $[92] !== isDark || $[93] !== onPageChange || $[94] !== scrollSnaps) {
    t35 = !!canScroll && <div {...getSpartanRevealElementProps({
      preset: SPARTAN_REVEAL_PRESET.content,
      role: SPARTAN_REVEAL_ROLE.fade,
      scrollOffset: SPARTAN_LINEUP_CONTROLS_REVEAL_OFFSET
    })}><SpartanSlidePagination testId={SPARTAN_LINEUP_TRACKING.pagination} itemTestIdPrefix={SPARTAN_LINEUP_TRACKING.paginationItem} theme={isDark ? "light" : "dark"} currentPage={currentPage} totalPages={scrollSnaps.length} onPageChange={onPageChange} wrapperClassName="mt-6 justify-center md:mt-8 lg:mt-10 lg:hidden" tracking={{
        eventName,
        group: SPARTAN_LINEUP_DATA_GA.section
      }} /></div>;
    $[89] = canScroll;
    $[90] = currentPage;
    $[91] = eventName;
    $[92] = isDark;
    $[93] = onPageChange;
    $[94] = scrollSnaps;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== panelId || $[97] !== t25 || $[98] !== t34 || $[99] !== t35) {
    t36 = <div id={panelId} role="tabpanel" aria-labelledby={t25} {...t26}>{t34}{t35}</div>;
    $[96] = panelId;
    $[97] = t25;
    $[98] = t34;
    $[99] = t35;
    $[100] = t36;
  } else {
    t36 = $[100];
  }
  let t37;
  if ($[101] !== canScroll) {
    t37 = cn("mt-6 flex flex-row items-center justify-between gap-4 md:gap-6", {
      "md:mt-8 lg:mt-10": canScroll
    });
    $[101] = canScroll;
    $[102] = t37;
  } else {
    t37 = $[102];
  }
  const t38 = isDark ? "text-gray-200" : "text-gray-600";
  let t39;
  if ($[103] !== canScroll || $[104] !== t38) {
    t39 = cn("text-caption where-small:text-body-2-fixed min-w-0", t38, {
      "flex-1": canScroll
    });
    $[103] = canScroll;
    $[104] = t38;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  let t40;
  if ($[106] !== remark) {
    t40 = {
      __html: remark
    };
    $[106] = remark;
    $[107] = t40;
  } else {
    t40 = $[107];
  }
  let t41;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = createDataAttributes({
      test: SPARTAN_LINEUP_TRACKING.remark
    });
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== t39 || $[110] !== t40) {
    t42 = <div className={t39} dangerouslySetInnerHTML={t40} {...t41} />;
    $[109] = t39;
    $[110] = t40;
    $[111] = t42;
  } else {
    t42 = $[111];
  }
  let t43;
  if ($[112] !== canUseSliderNavigation || $[113] !== eventName || $[114] !== nextBtnDisabled || $[115] !== onNextButtonClick || $[116] !== onPrevButtonClick || $[117] !== prevBtnDisabled || $[118] !== tone) {
    t43 = canUseSliderNavigation ? <SpartanSliderNav testId={SPARTAN_LINEUP_TRACKING.sliderNav} prevTestId={SPARTAN_LINEUP_TRACKING.sliderNavPrev} nextTestId={SPARTAN_LINEUP_TRACKING.sliderNavNext} onPrev={onPrevButtonClick} onNext={onNextButtonClick} isPrevDisabled={prevBtnDisabled} isNextDisabled={nextBtnDisabled} className="hidden shrink-0 justify-center lg:flex" buttonClassName={SPARTAN_PRODUCT_ICON_BUTTON_CLASS_NAME} color={tone === "dark" ? "white" : "black"} tracking={{
      eventName,
      group: SPARTAN_LINEUP_DATA_GA.section
    }} /> : null;
    $[112] = canUseSliderNavigation;
    $[113] = eventName;
    $[114] = nextBtnDisabled;
    $[115] = onNextButtonClick;
    $[116] = onPrevButtonClick;
    $[117] = prevBtnDisabled;
    $[118] = tone;
    $[119] = t43;
  } else {
    t43 = $[119];
  }
  let t44;
  if ($[120] !== t37 || $[121] !== t42 || $[122] !== t43) {
    t44 = <div className={t37}>{t42}{t43}</div>;
    $[120] = t37;
    $[121] = t42;
    $[122] = t43;
    $[123] = t44;
  } else {
    t44 = $[123];
  }
  let t45;
  if ($[124] !== t24 || $[125] !== t36 || $[126] !== t44) {
    t45 = <>{t24}{t36}{t44}</>;
    $[124] = t24;
    $[125] = t36;
    $[126] = t44;
    $[127] = t45;
  } else {
    t45 = $[127];
  }
  return t45;
};
function _temp(_api, event) {
  return !isCarouselInteractiveTarget(event.target);
}
function _temp2() {
  return new Set();
}